import { useMemo } from 'react';

import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import { useRentalManagerMediaCreditsWebApiHandler_getMediaCredits } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { MEDIA_CREDITS_TAG } from '../constants/MediaCreditsConstants';

import type {
  ClientAdProductType,
  CustomerAnalyticsMediaCreditPropertyDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type MediaCreditProperty = CustomerAnalyticsMediaCreditPropertyDto;

type FilterOption<TValue> = {
  label: string;
  value: TValue;
};

const getUniqueSortedOptions = <TValue>(
  properties: Array<MediaCreditProperty>,
  getOption: (property: MediaCreditProperty) => FilterOption<TValue> | undefined,
) => {
  const seenValues = new Set<TValue>();
  const options: Array<FilterOption<TValue>> = [];

  properties.forEach((property) => {
    const option = getOption(property);

    if (!option || seenValues.has(option.value)) {
      return;
    }

    seenValues.add(option.value);
    options.push(option);
  });

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

const getMetropolitanServiceAreaLabel = (address?: string) => {
  const [, city = '', stateAndZip = ''] = (address ?? '').split(',').map((value) => value.trim());
  const state = stateAndZip.replace(/\d{5}(-\d{4})?$/, '').trim();

  return [city, state].filter(Boolean).join(', ');
};

const matchesSearchText = (property: MediaCreditProperty, normalizedSearchText: string) =>
  !normalizedSearchText ||
  [property.propertyName, property.address, property.zrnNumber]
    .filter((value): value is string => Boolean(value))
    .some((value) => value.toLowerCase().includes(normalizedSearchText));

export const useMediaCreditsEligibility = (
  searchText: string,
  packageName?: ClientAdProductType,
  msaRegionId?: number,
) => {
  const { data, isLoading } = useRentalManagerMediaCreditsWebApiHandler_getMediaCredits({
    meta: {
      tags: [MEDIA_CREDITS_TAG],
      context: { api: 'rental-manager-api', details: 'useMediaCreditsEligibility' },
    },
  });

  const properties = useMemo(() => data?.properties ?? [], [data]);

  const packageOptions = useMemo(
    () =>
      getUniqueSortedOptions(properties, (property) => ({
        value: property.packageName,
        label: AdproductsConstants.ProductTypes[property.packageName] ?? property.packageName,
      })),
    [properties],
  );

  const metropolitanServiceAreaOptions = useMemo(
    () =>
      getUniqueSortedOptions(properties, (property) =>
        property.geo?.msaRegionId
          ? { value: property.geo.msaRegionId, label: getMetropolitanServiceAreaLabel(property.address) }
          : undefined,
      ),
    [properties],
  );

  const filteredProperties = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLowerCase();

    return properties.filter(
      (property) =>
        matchesSearchText(property, normalizedSearchText) &&
        (!packageName || property.packageName === packageName) &&
        (!msaRegionId || property.geo?.msaRegionId === msaRegionId),
    );
  }, [msaRegionId, packageName, properties, searchText]);

  const totalCreditAmount = useMemo(
    () => properties.reduce((total, property) => total + (property.creditAmount ?? 0), 0),
    [properties],
  );

  return {
    eligiblePropertyCount: properties.length,
    filteredProperties,
    isLoading,
    metropolitanServiceAreaOptions,
    packageOptions,
    totalCreditAmount,
  };
};
